/**
 * GAMIFICATION HOOKS
 *
 * React hooks for accessing and managing gamification state
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, getFirestore } from 'firebase/firestore';
import { firebaseApp } from '../firebase';
import { UserProgress, Achievement } from '../constants/gamification';
import gamificationService from '../services/gamificationService';

const db = getFirestore(firebaseApp);

/**
 * Hook to get and track user's gamification progress
 */
export function useUserProgress(userId: string | undefined) {
  const [progress, setProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Real-time listener for user progress
    const unsubscribe = onSnapshot(
      doc(db, 'userProgress', userId),
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setProgress({
            ...data,
            lastActivityDate: data.lastActivityDate?.toDate() || new Date(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as UserProgress);
        } else {
          // Initialize if doesn't exist
          try {
            const initialized = await gamificationService.initializeUserProgress(userId);
            setProgress(initialized);
          } catch (err) {
            setError(err as Error);
          }
        }
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  return { progress, loading, error };
}

/**
 * Hook to get user's achievements
 */
export function useUserAchievements(userId: string | undefined) {
  const [achievements, setAchievements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadAchievements = async () => {
      try {
        const userAchievements = await gamificationService.getUserAchievements(userId);
        setAchievements(userAchievements);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadAchievements();
  }, [userId]);

  return { achievements, loading };
}

/**
 * Hook to track user activity and update streaks
 */
export function useActivityTracker(userId: string | undefined) {
  const trackActivity = useCallback(
    async (activityType: 'login' | 'test' | 'resume_upload' | 'profile_edit' | 'course' | 'project') => {
      if (!userId) return null;

      try {
        const result = await gamificationService.trackActivity(userId, activityType);
        return result;
      } catch (error) {
        return null;
      }
    },
    [userId]
  );

  return { trackActivity };
}

/**
 * Hook to unlock achievements
 */
export function useAchievementUnlocker(userId: string | undefined) {
  const [recentUnlocks, setRecentUnlocks] = useState<Achievement[]>([]);

  const unlockAchievement = useCallback(
    async (achievementId: string, metadata?: any) => {
      if (!userId) return null;

      try {
        const result = await gamificationService.unlockAchievement(userId, achievementId, metadata);
        if (result.unlocked && result.achievement) {
          setRecentUnlocks((prev) => [...prev, result.achievement!]);

          // Clear after 5 seconds
          setTimeout(() => {
            setRecentUnlocks((prev) => prev.filter((a) => a.id !== achievementId));
          }, 5000);
        }
        return result;
      } catch (error) {
        return null;
      }
    },
    [userId]
  );

  return { unlockAchievement, recentUnlocks };
}

/**
 * Hook to get leaderboard data
 */
export function useLeaderboard(period: 'weekly' | 'monthly' | 'alltime' = 'alltime', limitCount: number = 50) {
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        const data = await gamificationService.getLeaderboard(period, limitCount);
        setLeaderboard(data);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();

    // Refresh every 5 minutes
    const interval = setInterval(loadLeaderboard, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [period, limitCount]);

  return { leaderboard, loading };
}

/**
 * Hook to get user's rank
 */
export function useUserRank(userId: string | undefined) {
  const [rank, setRank] = useState<{ rank: number; totalUsers: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const loadRank = async () => {
      try {
        const rankData = await gamificationService.getUserRank(userId);
        setRank(rankData);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadRank();

    // Refresh every 10 minutes
    const interval = setInterval(loadRank, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [userId]);

  return { rank, loading };
}

/**
 * Hook to check test achievements after test completion
 */
export function useTestAchievements(userId: string | undefined) {
  const checkAchievements = useCallback(
    async (testScore: number, testTime?: number) => {
      if (!userId) return [];

      try {
        const unlocked = await gamificationService.checkTestAchievements(userId, testScore, testTime);
        return unlocked;
      } catch (error) {
        return [];
      }
    },
    [userId]
  );

  return { checkAchievements };
}
